import KastAppClient, { buildSearchQuery, UserIdentityOutputDTO, UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { User } from "@kalyzee/kast-web-sdk/websocket";

export const getNormalUser = (user: UserOutputDTO | User) => {
  let lastname: string;
  let firstname: string;
  let avatar: string;
  let username: string;
  let email: string;
  if ("identity" in user) {
    lastname = user.identity?.lastname || "";
    firstname = user.identity?.firstname || "";
    avatar = user.identity?.avatar || "";
    username = user.identity?.username || "";
    email = user.identity?.email || "";
  } else {
    lastname = user.lastname || "";
    firstname = user.firstname || "";
    avatar = user.avatar || "";
    username = user.username || "";
    email = user.email || "";
  }
  const displayName = `${firstname || ""} ${lastname || ""}`.trim() || username || email;
  return { displayName, avatar };
};

/**
 * The user fields holding an identity, paired with the type they carry, in the
 * server's own pick priority (`IdentityIdFieldMap` declaration order) — the
 * order the API follows when nothing names an identity explicitly.
 *
 * `identity` backs both LOCAL and LOCAL_GUEST; the populated document says
 * nothing about which, so the field name is the only type information there is.
 */
export const USER_IDENTITY_FIELDS: { field: keyof UserOutputDTO; type: string }[] = [
  { field: "identity", type: "local" },
  { field: "googleIdentity", type: "google" },
  { field: "azureAdIdentity", type: "azure_ad" },
  { field: "casIdentity", type: "cas" },
  { field: "ltiIdentity", type: "lti" },
  { field: "jwtSsoIdentity", type: "jwt_sso" },
];

/**
 * The identity a user is best described by: the first one it actually carries,
 * in server priority order. Reading `user.identity` alone is wrong — the field
 * is present but null on an account whose only identity is external (Google-only
 * users are common), which leaves such a user labelled with a bare id.
 */
export const getUserPrimaryIdentity = (user: UserOutputDTO): UserIdentityOutputDTO | undefined => {
  for (const { field } of USER_IDENTITY_FIELDS) {
    const identity = user[field] as UserIdentityOutputDTO | undefined;
    if (identity) return identity;
  }
  return undefined;
};

/** Name to show for a user, whichever kind of identity carries it. */
export const getUserDisplayName = (user: UserOutputDTO) => {
  const identity = getUserPrimaryIdentity(user);
  return getNormalUser({ ...user, identity }).displayName || user.id;
};

/** Avatar url carried by the user's primary identity, empty when it has none. */
export const getUserAvatar = (user: UserOutputDTO) => (
  getNormalUser({ ...user, identity: getUserPrimaryIdentity(user) }).avatar
);

/** Email of the user's primary identity, when it carries one. */
export const getUserEmail = (user: UserOutputDTO) => getUserPrimaryIdentity(user)?.email;

/** Longer label for a search hit: display name, role and email when known. */
export const getUserLabel = (user: UserOutputDTO) => (
  [getUserDisplayName(user), user.role, getUserPrimaryIdentity(user)?.email]
    .filter((part) => !!part)
    .join(" · ")
);

/**
 * Free-text user search. The API searches the IDENTITY documents behind the
 * users, so the fields are identity ones; it answers 400 on a malformed `q`,
 * which `buildSearchQuery` prevents. `+` is the separator of that format — a
 * query carrying one would be read as a second parameter.
 *
 * Needs `user:read:any`, and `identity:read:any` for the search to run at all
 * (without it the API ignores `q` and answers the plain listing).
 */
export const USER_SEARCH_FIELDS = ["firstname", "lastname", "email", "username"];
export const USER_SEARCH_LIMIT = 8;

export const searchUsers = async (
  http: KastAppClient,
  query: string,
  limit: number = USER_SEARCH_LIMIT,
): Promise<UserOutputDTO[]> => {
  const cleaned = query.trim().replace(/\+/g, " ");
  if (!cleaned) return [];
  const response = await http.user.getAll({ q: buildSearchQuery(cleaned, USER_SEARCH_FIELDS), limit });
  return response.data ?? [];
};
